import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { smsMessageForEditor } from '../utils/smsEditor';
import { SmsOptionCard } from './sms/SmsOptionCard';
import { SmsOptionEditor } from './sms/SmsOptionEditor';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from './ui/alert-dialog';

const legacyOption = config => ({ id: 'default', name: 'Default SMS', message: smsMessageForEditor(config),
  description: config.description || 'When the caller asks for this information or agrees to receive it.', enabled: true });
const newId = () => `sms_${crypto.randomUUID()}`;

export const SendSmsSettings = ({ config = {}, onChange, onEditingChange, inputBg, cardBorder, textPrimary, textSecondary }) => {
  const options = Array.isArray(config.options) ? config.options : smsMessageForEditor(config) ? [legacyOption(config)] : [];
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  useEffect(() => { onEditingChange?.(!!editing); return () => onEditingChange?.(false); }, [editing, onEditingChange]);
  const colors = { inputBg, cardBorder, textPrimary, textSecondary };
  const commit = updated => {
    // Keep legacy fields as compatibility data for non-call consumers; named
    // in-call tools use only the validated options. No migration on opening.
    onChange({ ...config, options: updated });
  };
  const edit = option => setEditing({ ...option });
  const save = option => {
    const exists = options.some(o => o.id === option.id);
    if (!exists && options.length >= 5) return;
    commit(exists ? options.map(o => o.id === option.id ? option : o) : [...options, option]);
    setEditing(null);
  };
  const duplicate = option => {
    let name = `${option.name.slice(0, 68)} (copy)`, n = 2;
    while (options.some(o => o.name.toLowerCase() === name.toLowerCase())) name = `${option.name.slice(0, 65)} (copy ${n++})`;
    edit({ ...option, id: newId(), name, enabled: false });
  };
  return <div className="min-w-0 space-y-4" data-testid="named-send-sms-settings">
    {editing ? <SmsOptionEditor key={editing.id} initial={editing} names={options.filter(o => o.id !== editing.id).map(o => o.name)} onSave={save} onCancel={() => setEditing(null)} {...colors} /> : <>
      <div className="flex items-center justify-between gap-3">
        <p data-testid="sms-options-count" className={`text-sm ${textSecondary}`}>{options.length} / 5 options</p>
        <button type="button" data-testid="sms-option-add" onClick={() => edit({ id: newId(), name: '', description: '', message: '', enabled: true })} disabled={options.length >= 5} title={options.length >= 5 ? 'Five SMS options maximum' : 'Add SMS option'} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-teal-400 text-gray-950 hover:bg-teal-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><Plus size={15} />Add SMS</button>
      </div>
      <div className="space-y-3">
        {options.map(option => <SmsOptionCard key={option.id} option={option} onEdit={() => edit(option)} onDuplicate={() => duplicate(option)} onDelete={() => setDeleting(option)} canDuplicate={options.length < 5} {...colors} />)}
        {!options.length && <p data-testid="sms-options-empty" className={`py-8 text-sm text-center ${textSecondary}`}>No SMS options saved.</p>}
      </div>
      <p data-testid="send-sms-call-limit" className={`text-xs ${textSecondary}`}>{Array.isArray(config.options) ? 'Up to 2 resources per call · Each option once' : 'One message per call'} · Sent through LeadConnector</p>
      <AlertDialog open={!!deleting} onOpenChange={open => { if (!open) setDeleting(null); }}>
        <AlertDialogContent data-testid="sms-option-delete-confirm" className="z-[80] w-[calc(100%_-_2rem)] max-w-md rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="sms-option-delete-name" className="break-words [overflow-wrap:anywhere]">Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription data-testid="sms-option-delete-description">This removes the option from the agent’s draft.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sms-option-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sms-option-delete-confirm-button" onClick={() => { commit(options.filter(o => o.id !== deleting.id)); setDeleting(null); }} className="text-white bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>}
  </div>;
};